import { Component, h, Host, Prop, State, Watch, Element, Method } from '@stencil/core';
import Swiper from 'swiper';
import { Navigation, Pagination, Controller, Keyboard } from 'swiper/modules';
import { v4 as uuidv4 } from 'uuid';
import { $$$, getRenderedImageRect, isCursorOverRenderedImage, geti18n } from '../../../utils/utils';
import { ProductCarouselMedia } from './product-carousel-media';

@Component({
  tag: 'lilu-product-img-carousel',
  styleUrl: 'lilu-product-img-carousel.scss',
  shadow: true,
})
export class LiluProductImgCarousel {
  @Prop() media: string;
  @Prop() isZoomable: boolean = false;
  @Prop() hasImages: number = 0;
  @Prop() hasVideos: number = 0;
  @Prop() dataTitle: string = null;
  @Prop() dataAlt: string = '';

  @State() internalMedia: ProductCarouselMedia[] = [];
  @State() zoomOpen: boolean = false;
  @State() showControls = false;

  @Element() el: HTMLElement;

  swiperId = uuidv4();

  swiperSelector = `swiper-id-${this.swiperId}`;
  swiperPagination = `swiper-pagination-${this.swiperId}`;
  swiperNextEl = `swiper-button-next-${this.swiperId}`;
  swiperPrevEl = `swiper-button-prev-${this.swiperId}`;

  private mainSlider: Swiper;
  protected static screenSizeThreshold = 992;

  private imageZoomView: HTMLDivElement;
  private lightboxElement = null;
  private activeSlide: HTMLElement = document.querySelector('lilu-product-img-carousel').shadowRoot.querySelector('.swiper-slide-active');
  private activeMediaType: string;
  private zoomImageWidth;
  private zoomImageUrl = this.activeSlide ? this.activeSlide.querySelector('img').getAttribute('data-zoom-url') : null;
  offsetWrapper: { left: number; top: number };
  readonly magnificationDefault = 1.66666667;
  private imageZoomCursor;
  private offset: any = 0;
  private imageWidth = 0;
  private imageHeight = 0;
  private cursorSize: any = 0;
  private magnification = 0;

  @Method()
  async open(imageIndex) {
    this.mainSlider.slideTo(imageIndex, 0, true);
    this.el.shadowRoot.querySelector('lilu-modal').open();
  }

  @Watch('media')
  parseImages() {
    if (this.media !== undefined) {
      this.internalMedia = JSON.parse(this.media);
      if (this.internalMedia.length > 1) this.showControls = true;
    }
  }

  componentWillLoad() {
    this.parseImages();
  }

  componentDidLoad() {
    this.lightboxElement = this.el.shadowRoot.querySelector('lilu-lightbox');

    const swiperElement: HTMLElement = this.el.shadowRoot.querySelector(`#${this.swiperSelector}`);
    const swiperPrevElement: HTMLElement = this.el.shadowRoot.querySelector(`#${this.swiperPrevEl}`);
    const swiperNextElement: HTMLElement = this.el.shadowRoot.querySelector(`#${this.swiperNextEl}`);
    const swiperPagination: HTMLElement = this.el.shadowRoot.querySelector(`#${this.swiperPagination}`);

    new Swiper(swiperElement, {
      modules: [Navigation, Pagination, Keyboard, Controller],
      slidesPerView: 1,
      loop: $$$('.mySwiper .swiper-slide').length > 1,
      watchOverflow: true,
      navigation: {
        prevEl: swiperPrevElement,
        nextEl: swiperNextElement,
      },
      keyboard: {
        enabled: true,
        onlyInViewport: false,
      },
      pagination: {
        el: swiperPagination,
        clickable: true,
        dynamicBullets: true,
        dynamicMainBullets: 7,
      },
      on: {
        click: swiper => {
          this.openLightBox(swiper.realIndex);
        },
      },
    });

    this.getAllImagesVideos();
  }

  componentDidRender() {
    this.imageZoomView = this.el.shadowRoot.querySelector('.image-gallery__image-zoom-view');
  }

  openLightBox(index) {
    this.lightboxElement.open(index);
  }

  summaryOpenLightbox() {
    let activeElementIndex = parseInt($$$('.mySwiper .swiper-slide.swiper-slide-active')[0].getAttribute('data-swiper-slide-index'));
    this.lightboxElement.open(activeElementIndex);
  }

  renderImageZoomViewer() {
    let screenSize = window.innerWidth;
    if (this.zoomOpen && screenSize >= LiluProductImgCarousel.screenSizeThreshold && this.zoomImageUrl) {
      let productWrapperSR: any = document.querySelector('lilu-product-wrapper').shadowRoot;
      let productDetails: any = productWrapperSR.querySelector('.product-details');
      let imageZoomView: any = document.querySelector('lilu-product-img-carousel').shadowRoot.querySelector('.image-gallery__image-zoom-view');
      const renderedImageRect = getRenderedImageRect(this.activeSlide.querySelector('img'));
      this.imageWidth = renderedImageRect.width;
      this.imageHeight = this.activeSlide.querySelector('img').clientHeight;

      // calculate magnification based on available image sizes
      this.magnification = this.zoomImageWidth ? this.zoomImageWidth / this.imageWidth : this.magnificationDefault;

      // calculate viewer size
      const viewerWidth = productDetails.clientWidth;
      let viewerHeight = this.imageHeight;

      // calculate cursor size
      this.cursorSize = [viewerWidth / this.magnification, viewerHeight / this.magnification];

      // set cursor size and position
      this.imageZoomCursor.style.width = Math.round(this.cursorSize[0]).toString() + 'px';
      this.imageZoomCursor.style.height = Math.round(this.cursorSize[1]).toString() + 'px';

      imageZoomView.style.width = viewerWidth.toString() + 'px';
      imageZoomView.style.height = viewerHeight.toString() + 'px';

      return (
        <picture style={{ width: (this.imageWidth * this.magnification).toString() + 'px', height: (this.imageHeight * this.magnification).toString() + 'px' }}>
          <img
            style={{ width: (this.imageWidth * this.magnification).toString() + 'px', height: (this.imageHeight * this.magnification).toString() + 'px' }}
            src={this.zoomImageUrl}
            {...(this.dataTitle !== null ? { title: this.dataTitle } : {})}
            alt={this.dataAlt}
          ></img>
        </picture>
      );
    } else if (this.zoomOpen && screenSize < LiluProductImgCarousel.screenSizeThreshold) {
      // reset cursorSize to `0` to disable the cursor on small screens
      this.cursorSize = 0;

      Object.assign(this.imageZoomCursor.style, {
        width: 0,
        height: 0,
      });
    }
    return null;
  }

  destroyImagezoomViewer() {
    this.zoomOpen = false;
    this.imageZoomCursor.style.display = 'none';
    const activeImage = this.activeSlide.querySelector('img');
    activeImage.style.cursor = 'default';

    Object.assign(this.imageZoomView.style, {
      width: '0',
      height: '0',
    });
  }

  startImageZoomViewer() {
    this.activeSlide = document.querySelector('lilu-product-img-carousel').shadowRoot.querySelector('.swiper-slide-active');
    this.activeMediaType = this.activeSlide.querySelector('img').getAttribute('data-media-type');

    if (this.activeMediaType !== 'image') {
      // disables zoom viewer for non-image media
      return;
    }

    this.zoomImageWidth = this.activeSlide ? this.activeSlide.querySelector('img').getAttribute('data-zoom-image-width') : null;
    this.zoomImageUrl = this.activeSlide ? this.activeSlide.querySelector('img').getAttribute('data-zoom-url') : null;
    this.imageZoomCursor = document.querySelector('lilu-product-img-carousel').shadowRoot.querySelector('.image-gallery__image-zoom-cursor');
    this.zoomOpen = true;
    const activeImage = this.activeSlide.querySelector('img');
    activeImage.style.cursor = 'default';
  }

  renderMouseMove(event) {
    const activeImage = this.activeSlide.querySelector('img');
    const isCursorOverImage = isCursorOverRenderedImage(activeImage, event);

    if (!this.zoomOpen && isCursorOverImage && this.activeMediaType === 'image') {
      this.zoomOpen = true;
      return;
    } else if (this.zoomOpen && !isCursorOverImage) {
      this.destroyImagezoomViewer();
      return;
    } else if (!this.zoomOpen || !this.cursorSize) {
      return;
    }

    let renderedImageRect = getRenderedImageRect(activeImage);
    let boundingClientRectWrapper = this.activeSlide.getBoundingClientRect();
    this.offset = { left: renderedImageRect.left, top: renderedImageRect.top };
    this.offsetWrapper = { left: renderedImageRect.left - boundingClientRectWrapper.left, top: renderedImageRect.top - boundingClientRectWrapper.top };
    var posX = event.clientX - this.offset.left;
    var posY = event.clientY - this.offset.top;
    let imageZoomView;
    if (posX < 0 + this.cursorSize[0] / 2) {
      posX = this.cursorSize[0] / 2; //limit cursor-box-left to far left of image
    } else if (posX + this.cursorSize[0] / 2 > this.imageWidth) {
      posX = this.imageWidth - this.cursorSize[0] / 2; //limit cursor-box-right to the far right of image
    }
    if (posY < 0 + this.cursorSize[1] / 2) {
      posY = this.cursorSize[1] / 2; //limit cursor-box-top to top of image
    } else if (posY + this.cursorSize[1] / 2 > this.imageHeight) {
      posY = this.imageHeight - this.cursorSize[1] / 2; //limit cursor-box-bottom to bottom of image
    }
    // set recalculated image and cursor position
    activeImage.style.cursor = '';
    this.imageZoomCursor.style.display = 'block';
    this.imageZoomCursor.style.top = Math.floor(posY - this.cursorSize[1] / 2 + this.offsetWrapper.top).toString() + 'px';
    this.imageZoomCursor.style.left = Math.floor(posX - this.cursorSize[0] / 2 + this.offsetWrapper.left).toString() + 'px';

    if (!imageZoomView) {
      imageZoomView = document.querySelector('lilu-product-img-carousel').shadowRoot.querySelector('.image-gallery__image-zoom-view');
    }
    if (imageZoomView && imageZoomView.querySelector('picture')) {
      imageZoomView.querySelector('img').style.bottom = Math.floor((posY - this.cursorSize[1] / 2) * this.magnification).toString() + 'px';
      imageZoomView.querySelector('img').style.right = Math.floor((posX - this.cursorSize[0] / 2) * this.magnification).toString() + 'px';
    }
  }

  getAllImagesVideos() {
    const array = JSON.parse(this.media);

    array.forEach(item => {
      if (item.videoURL === undefined) {
        this.hasImages++;
      } else {
        this.hasVideos++;
      }
    });
  }

  render() {
    const slides = [];

    this.internalMedia.forEach(media => {
      slides.push(
        <div class="swiper-slide">
          {this.isZoomable ? (
            <img
              src={media.imageURL}
              onMouseEnter={() => this.startImageZoomViewer()}
              onMouseMove={e => this.renderMouseMove(e)}
              onMouseLeave={() => this.destroyImagezoomViewer()}
              data-media-type={media.isVideo ? 'video' : 'image'}
              data-zoom-url={media.zoomImageUrl}
              data-zoom-image-width={media.zoomImageWidth}
              {...(this.dataTitle !== null ? { title: this.dataTitle } : {})}
              alt={this.dataAlt}
            />
          ) : (
            <img src={media.imageURL} data-media-type={media.isVideo ? 'video' : 'image'} {...(this.dataTitle !== null ? { title: this.dataTitle } : {})} alt={this.dataAlt} />
          )}
          {media.videoURL ? (
            <div class="circle">
              <div class="arrow-right"></div>
            </div>
          ) : (
            ''
          )}
        </div>,
      );
    });

    return (
      <Host>
        <div class="container">
          <div class="top-left">
            <slot name="top-left"></slot>
          </div>
          <div class="top-right">
            <slot name="top-right"></slot>
          </div>
          <div class="bottom-left">
            <slot name="bottom-left"></slot>
          </div>
          <div class="bottom-right">
            <slot name="bottom-right"></slot>
          </div>
          <div class="image-gallery__image-zoom-view">{this.renderImageZoomViewer()}</div>

          <div class="swiper mySwiper" id={this.swiperSelector}>
            <span class="image-gallery__image-zoom-cursor"></span>
            <div class="swiper-wrapper">{slides}</div>
            <div class={`swiper-button-next ${this.showControls ? 'show' : ''}`} id={this.swiperNextEl}>
              <lilu-rounded-icon-button size="size-lg" color="bleached-silk" icon="icon-chevron-right" icon-size="size-14 lg-size-16" icon-color="nero"></lilu-rounded-icon-button>
            </div>
            <div class={`swiper-button-prev ${this.showControls ? 'show' : ''}`} id={this.swiperPrevEl}>
              <lilu-rounded-icon-button size="size-lg" color="bleached-silk" icon="icon-chevron-left" icon-size="size-14 lg-size-16" icon-color="nero"></lilu-rounded-icon-button>
            </div>
            {/* bottom START */}
            <div class="swiper-bottom">
              <div class="swiper-pagination" id={this.swiperPagination}></div>
              <div class="swiper-summary" onClick={() => this.summaryOpenLightbox()}>
                {this.hasImages > 0 ? (
                  <div>
                    <span>{this.hasImages}</span> {this.hasImages > 1 ? geti18n('swiper.images') : geti18n('swiper.image')}
                  </div>
                ) : (
                  ``
                )}
                {this.hasVideos > 0 ? (
                  <div>
                    <span>{this.hasVideos}</span> {this.hasVideos > 1 ? geti18n('swiper.videos') : geti18n('swiper.video')}
                  </div>
                ) : (
                  ``
                )}
              </div>
            </div>
            {/* bottom END */}
          </div>
        </div>
        <lilu-lightbox open-modal="false" slot="lightbox" media={this.media}></lilu-lightbox>
      </Host>
    );
  }
}
